import { useState, useMemo } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  Mail,
  Phone,
  Shield,
  LogIn,
  LogOut,
  Monitor,
  Globe,
  Briefcase,
  Calendar,
  CreditCard,
  IdCard,
  Clock,
  User as UserIcon,
  Key,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { trpcClient } from "@/lib/trpc";
import { authApi } from "@/lib/api-client";
import { getStatusConfig } from "@/config/status-registry";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { EmployeeContractList } from "@/components/system/users/EmployeeContractList";
import { EmployeeContractForm } from "@/components/system/users/EmployeeContractForm";
import { InteractiveStatusSwitch } from "@/components/ui/interactive-status-switch";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { Progress } from "@/components/ui/progress";
import { getUploadUrl, formatDate, renderDetailValue } from "@/lib/utils";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";
import ActivityLogSection from "@/components/shared/ActivityLogSection";
import { DetailField as F } from "@/components/shared/DetailField";


/** Parse user agent to a readable device/browser summary */
function parseUA(ua?: string): string {
  if (!ua) return "—";
  const isMobile = /mobile|android|iphone/i.test(ua);
  const browser = /chrome/i.test(ua)
    ? "Chrome"
    : /firefox/i.test(ua)
      ? "Firefox"
      : /safari/i.test(ua)
        ? "Safari"
        : /edge/i.test(ua)
          ? "Edge"
          : "Khác";
  const os = /windows/i.test(ua)
    ? "Windows"
    : /mac/i.test(ua)
      ? "macOS"
      : /linux/i.test(ua)
        ? "Linux"
        : /android/i.test(ua)
          ? "Android"
          : /iphone|ipad/i.test(ua)
            ? "iOS"
            : "";
  return `${browser} ${isMobile ? "📱" : "💻"} ${os}`;
}

const getRoleLabel = (role: string) => {
  switch (role?.toUpperCase()) {
    case "ADMIN":
      return "Quản trị viên";
    case "MANAGER":
      return "Quản lý";
    case "SALES_MANAGER":
      return "Trưởng phòng KD";
    case "SALES":
      return "Kinh doanh";
    case "PLANNER":
      return "Kế hoạch";
    case "WAREHOUSE":
      return "Thủ kho";
    case "DELIVERY":
      return "Giao hàng";
    case "QC":
      return "Chất lượng (QC)";
    case "TECH":
      return "Kỹ thuật";
    case "OPERATOR":
      return "Vận hành";
    case "USER":
      return "Nhân viên";
    default:
      return role;
  }
};

const getDepartmentLabel = (dept: string) => {
  switch (dept?.toUpperCase()) {
    case "IT":
      return "Công Nghệ Thông Tin";
    case "SALES":
      return "Kinh Doanh";
    case "PROD":
      return "Sản Xuất";
    case "QC":
      return "Kiểm Soát Chất Lượng";
    case "WH":
      return "Kho Vận";
    case "TECH":
      return "Kỹ Thuật";
    case "MAINT":
      return "Bảo Trì";
    case "BOARD":
      return "Ban Giám Đốc";
    case "FIN":
      return "Kế Toán";
    case "HR":
      return "Hành Chính Nhân Sự";
    default:
      return dept;
  }
};

export default function UserDetailPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, hasPermission } = useRoleAccess();
  const canViewUsers = isAdmin || hasPermission("users", "view");
  const canUpdateUsers = isAdmin || hasPermission("users", "update");
  const canDeleteUsers = isAdmin || hasPermission("users", "delete");

  const canViewHR = isAdmin || hasPermission("users", "view");
  const { userId } = useParams({ strict: false }) as { userId: string };
  const [showDelete, setShowDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [activeTab, setActiveTab] = useState("info");
  // Hợp đồng lao động: thêm / sửa ngay trên tab (trước 04/11/2026 danh sách không gắn form nên không thêm / sửa được)
  const [contractForm, setContractForm] = useState<{ open: boolean; data?: any }>({ open: false });
  const [savingContract, setSavingContract] = useState(false);

  const resetPasswordMutation = useMutation({
    mutationFn: (input: { id: string }) =>
      trpcClient.users.resetPassword.mutate(input),
    onSuccess: (data: any) => {
      setShowResetPassword(false);
    },
    onError: (err: any) => {
      toast.error(err.message || "Có lỗi xảy ra khi đặt lại mật khẩu");
    },
  });

  const handleDelete = async (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      await trpcClient.users.delete.mutate({ id: userId });
      setShowDelete(false);

      await queryClient.invalidateQueries({ queryKey: ["users"] });
      await queryClient.invalidateQueries({ queryKey: ["users-lookup"] });
      navigate({ to: "/users" });
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi xóa");
      setIsDeleting(false);
    }
  };

  const {
    data: user,
    isLoading,
    error,
    refetch: refetchDetail,
  } = useQuery({
    queryKey: ["user", userId],
    queryFn: async () => trpcClient.users.getById.query({ id: userId }),
    enabled: canViewUsers && !!userId,
  });

  // Login/logout activity for this user
  const { data: userActivity } = useQuery({
    queryKey: ["user-activity", userId],
    queryFn: async () => {
      const res: any = await authApi.getUserActivity(200);
      const all: any[] = Array.isArray(res) ? res : res?.data || [];
      return all.filter((a: any) => a.entityId === (user as any)?.id);
    },
    enabled: activeTab === "history" && !!(user as any)?.id,
  });

  // Active sessions
  const { data: activeSessions } = useQuery({
    queryKey: ["user-sessions", userId],
    queryFn: async () => {
      const res: any = await authApi.getActiveSessions();
      const all: any[] = Array.isArray(res) ? res : res?.data || [];
      return all.filter((s: any) => s.userId === (user as any)?.id);
    },
    enabled: activeTab === "history" && !!(user as any)?.id,
  });

  // Fetch contracts to calculate progress
  const { data: contractsData } = useQuery({
    queryKey: ["employeeContracts", userId],
    queryFn: () =>
      trpcClient.employeeContracts.list.query({ userId, page: 1, limit: 10 }),
    enabled: canViewHR && !!userId,
  });

  const contracts = contractsData?.items || [];

  const currentContract = useMemo(() => {
    if (!contracts.length) return null;
    const active = contracts.find((c: any) => c.status === "active");
    if (active) return active;
    // if no active, pick the latest end date
    return [...contracts].sort((a: any, b: any) => {
      if (!a.endDate) return -1;
      if (!b.endDate) return 1;
      return new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
    })[0];
  }, [contracts]);

  const d = user as any;

  if (!canViewUsers) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "Người dùng", to: "/users" }]}
        title="Không có quyền truy cập"
      >
        <div className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-4" />
          <p className="text-slate-600 mb-4">
            Tài khoản của bạn không được cấp quyền xem thông tin người dùng (users.view).
          </p>
          <Button onClick={() => navigate({ to: "/" })}>Về trang chủ</Button>
        </div>
      </StandardDetailLayout>
    );
  }

  // Gõ/dán URL có id sai (404) hoặc không tải được: không hiển thị khung tải vô hạn
  if (!isLoading && !d) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "Người dùng", to: "/users" }]}
        entityLabel="Người dùng"
        backTo="/users"
        codeOrId={userId}
        error={error}
        onRetry={() => refetchDetail()}
      />
    );
  }
  if (isLoading || !d) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "Người dùng", to: "/users" }]}
        title="Đang tải..."
      >
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
        </div>
      </StandardDetailLayout>
    );
  }

  if (error) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "Người dùng", to: "/users" }]}
        title="Lỗi"
      >
        <div className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-4" />
          <Button onClick={() => navigate({ to: "/users" })}>Quay lại</Button>
        </div>
      </StandardDetailLayout>
    );
  }

  // Calculate contract progress
  let contractProgress = 0;
  let daysRemaining = 0;
  let totalDays = 0;
  let contractStatusText = "Không xác định";
  let progressColor = "bg-slate-200";

  if (currentContract && currentContract.startDate) {
    const start = new Date(currentContract.startDate).getTime();
    const end = currentContract.endDate
      ? new Date(currentContract.endDate).getTime()
      : 0;
    const now = new Date().getTime();

    if (!end) {
      contractProgress = 100;
      contractStatusText = "Vô thời hạn";
      progressColor = "bg-emerald-500";
    } else {
      totalDays = Math.max(
        1,
        Math.floor((end - start) / (1000 * 60 * 60 * 24)),
      );
      const elapsed = Math.max(
        0,
        Math.floor((now - start) / (1000 * 60 * 60 * 24)),
      );
      daysRemaining = Math.max(0, totalDays - elapsed);
      contractProgress = Math.min(
        100,
        Math.max(0, (elapsed / totalDays) * 100),
      );

      if (now > end) {
        contractProgress = 100;
        contractStatusText = "Đã hết hạn";
        progressColor = "bg-rose-500";
        daysRemaining = 0;
      } else if (daysRemaining <= 30) {
        contractStatusText = `Còn ${daysRemaining} ngày (Sắp hết hạn)`;
        progressColor = "bg-amber-500";
      } else {
        contractStatusText = `Còn ${daysRemaining} ngày`;
        progressColor = "bg-indigo-500";
      }
    }
  } else {
    contractStatusText = "Chưa có hợp đồng";
  }

  return (
    <>
      <StandardDetailLayout
        breadcrumbs={[{ label: "Người dùng", to: "/users" }]}
        title={d.employeeCode || d.username || d.userId}
        entityId={d.id || userId}
        entityType="user"
        timestamps={{ created: d.createdAt, updated: d.updatedAt }}
        status={
          <InteractiveStatusSwitch
            initialStatus={d.status}
            entityName="người dùng này"
            disabled={!canUpdateUsers}
            onToggle={async (newStatus) => {
              await trpcClient.users.update.mutate({
                id: d.id,
                data: { status: newStatus },
              });
              queryClient.invalidateQueries({ queryKey: ["user", userId] });
            }}
          />
        }
        actions={
          <div className="flex gap-1.5">
            {canUpdateUsers && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowResetPassword(true)}
                className="text-orange-600 border-orange-200 hover:bg-orange-50"
              >
                <Key size={14} className="mr-1.5" /> Reset Mật Khẩu
              </Button>
            )}
            {canUpdateUsers && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate({ to: `/users/${d.userId}/edit` })}
              >
                <Pencil size={14} className="mr-1.5" /> Sửa hồ sơ
              </Button>
            )}
            {canDeleteUsers && (
              <Button
                variant="outline"
                size="sm"
                className="text-rose-600 border-rose-200 hover:bg-rose-50"
                onClick={() => setShowDelete(true)}
              >
                <Trash2 size={14} className="mr-1.5" /> Xoá
              </Button>
            )}
          </div>
        }
        onBack={() => navigate({ to: "/users" })}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-6 p-2">
          {/* Left Column: Profile Card & Widgets */}
          <div className="flex flex-col gap-6 md:col-span-1 xl:col-span-1">
            {/* Profile Identity Card */}
            <Card className="border border-border shadow-xs overflow-hidden">
              <div
                className={`h-20 w-full ${d.status === "ACTIVE" ? "bg-emerald-500/15 border-b border-emerald-500/20" : "bg-muted border-b border-border"}`}
              />
              <div className="px-6 pb-6 relative">
                <div className="absolute -top-10 left-6 p-1 bg-card border border-border rounded-xl shadow-xs">
                  <div className="w-20 h-20 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-2xl font-bold overflow-hidden">
                    {d.avatar ? (
                      <img
                        src={getUploadUrl(d.avatar)}
                        alt={d.fullName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <UserIcon className="w-12 h-12 text-indigo-300 stroke-[1.5]" />
                    )}
                  </div>
                </div>

                <div className="pt-12">
                  <h2 className="text-xl font-bold text-slate-900 leading-tight">
                    {d.fullName}
                  </h2>
                  <p className="text-sm font-medium text-indigo-600 mt-1">
                    {d.position || "Chưa rõ chức danh"}
                  </p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <Badge
                      variant="secondary"
                      className="bg-slate-100 text-slate-700 hover:bg-slate-200 font-medium border-none shadow-none"
                    >
                      {getDepartmentLabel(d.department)}
                    </Badge>
                    <Badge
                      variant="outline"
                      className="text-slate-600 flex items-center gap-1 font-medium"
                    >
                      <Shield className="w-3 h-3" /> {getRoleLabel(d.role)}
                    </Badge>
                  </div>

                  <div className="mt-6 space-y-3 border-t border-slate-100 pt-4">
                    <div className="flex items-center gap-3 text-sm text-slate-600">
                      <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                      <a
                        href={`mailto:${d.email}`}
                        className="hover:text-indigo-600 break-words whitespace-normal"
                      >
                        {d.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-600">
                      <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                      <span>{d.phone || "Chưa cập nhật SĐT"}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-600">
                      <IdCard className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="font-mono bg-slate-50 px-1.5 py-0.5 rounded text-xs border border-slate-100">
                        {d.employeeCode || d.userId}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Contract Widget */}
            {canViewHR && (
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Clock className="w-4 h-4 text-indigo-500" />
                    Thời hạn hợp đồng
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {currentContract ? (
                    <div className="space-y-4">
                      <div className="flex justify-between items-end">
                        <div className="text-2xl font-bold text-slate-800 tracking-tight">
                          {daysRemaining > 0
                            ? daysRemaining
                            : contractProgress === 100 &&
                                !currentContract.endDate
                              ? "∞"
                              : "0"}
                          <span className="text-sm font-normal text-slate-500 ml-1">
                            ngày
                          </span>
                        </div>
                        <Badge
                          variant="outline"
                          className={`font-semibold border-none ${
                            contractProgress === 100 && currentContract.endDate
                              ? "bg-rose-100 text-rose-700"
                              : daysRemaining <= 30 && currentContract.endDate
                                ? "bg-amber-100 text-amber-700"
                                : "bg-indigo-50 text-indigo-700"
                          }`}
                        >
                          {contractStatusText}
                        </Badge>
                      </div>

                      <div className="space-y-1.5">
                        <Progress
                          value={contractProgress}
                          indicatorColor={progressColor}
                          className="h-2 bg-slate-100"
                        />
                        <div className="flex justify-between text-[11px] font-medium text-slate-400">
                          <span>{formatDate(currentContract.startDate)}</span>
                          <span>
                            {currentContract.endDate
                              ? formatDate(currentContract.endDate)
                              : "Vô thời hạn"}
                          </span>
                        </div>
                      </div>
                      <div className="text-xs text-slate-500 font-medium break-words whitespace-normal pt-2 border-t border-slate-100">
                        HĐ: {currentContract.contractNumber} (
                        {currentContract.type})
                      </div>
                    </div>
                  ) : (
                    <div className="py-6 text-center text-slate-400 text-sm">
                      Chưa có dữ liệu hợp đồng
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          <div className="md:col-span-2 xl:col-span-3">
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="mb-4">
                <TabsTrigger value="info">Thông tin chung</TabsTrigger>
                {canViewHR && (
                  <TabsTrigger value="contracts">
                    Hợp đồng & Pháp lý
                  </TabsTrigger>
                )}
                <TabsTrigger value="history">Lịch sử hệ thống</TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="space-y-6">
                {/* Basic Info & Contact Card */}
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader className="pb-3 border-b border-slate-100 bg-slate-50">
                    <CardTitle className="text-[15px] flex items-center gap-2">
                      <UserIcon className="w-4 h-4 text-slate-400" /> Thông tin
                      cơ bản & Liên hệ
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-y-6 gap-x-4">
                    <F
                      label="Mã nhân viên"
                      value={d.employeeCode || d.userId}
                      mono
                    />
                    <F label="Họ và tên" value={d.fullName} />
                    <F label="Email" value={d.email} />
                    <F label="Số điện thoại" value={d.phone} />
                    <F
                      label="Giới tính"
                      value={
                        d.gender === "MALE"
                          ? "Nam"
                          : d.gender === "FEMALE"
                            ? "Nữ"
                            : d.gender === "OTHER"
                              ? "Khác"
                              : "—"
                      }
                    />
                    <F
                      label="Ngày sinh"
                      value={d.dateOfBirth ? formatDate(d.dateOfBirth) : "—"}
                      icon={Calendar}
                    />
                    <F
                      label="Trạng thái"
                      value={
                        d.status === "ACTIVE"
                          ? "Đang hoạt động"
                          : d.status === "INACTIVE"
                            ? "Không hoạt động"
                            : "Tạm khóa"
                      }
                    />
                  </CardContent>
                </Card>

                {/* Role & Organization Card */}
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader className="pb-3 border-b border-slate-100 bg-slate-50">
                    <CardTitle className="text-[15px] flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-slate-400" /> Vai trò &
                      Tổ chức
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-y-6 gap-x-4">
                    <F label="Vai trò hệ thống" value={getRoleLabel(d.role)} />
                    <F
                      label="Phòng ban"
                      value={getDepartmentLabel(d.department)}
                    />
                    <F label="Chức vụ" value={d.position} />
                    <F label="Bộ phận chi tiết" value={d.section} />
                  </CardContent>
                </Card>

                {/* Legal & Contracts Card */}
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader className="pb-3 border-b border-slate-100 bg-slate-50">
                    <CardTitle className="text-[15px] flex items-center gap-2">
                      <IdCard className="w-4 h-4 text-slate-400" /> Hồ sơ Pháp
                      lý & Thông tin phụ
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-y-6 gap-x-4">
                    <F label="Số CCCD" value={d.idCardNumber} mono />
                    <F
                      label="Ngày cấp CCCD"
                      value={
                        d.idCardIssueDate ? formatDate(d.idCardIssueDate) : "—"
                      }
                      icon={Calendar}
                    />
                    <F label="Nơi cấp CCCD" value={d.idCardIssuePlace} />
                    <F label="Mã số thuế" value={d.taxNumber} mono />
                    <F label="Số BHXH" value={d.socialInsuranceNumber} mono />
                    <F
                      label="Ngày gia nhập"
                      value={d.joinDate ? formatDate(d.joinDate) : "—"}
                      icon={Calendar}
                    />
                    <F
                      label="Ngày chính thức"
                      value={d.officialDate ? formatDate(d.officialDate) : "—"}
                      icon={Calendar}
                    />
                    <F label="Loại hợp đồng" value={d.contractStatus} />
                    <F label="Ngân hàng" value={d.bankName} />
                    <F label="Số tài khoản" value={d.bankAccountNumber} mono />
                    <F label="Chi nhánh NH" value={d.bankBranch} />
                    <F label="SĐT Khẩn cấp" value={d.emergencyContact} />
                  </CardContent>
                </Card>
              </TabsContent>

              {canViewHR && (
                <TabsContent value="contracts" className="space-y-6">
                  {/* Contracts List */}
                  <Card className="border-slate-200 shadow-sm xl:col-span-2">
                    <CardHeader className="pb-3 border-b border-slate-100 flex flex-row items-center justify-between bg-slate-50">
                      <CardTitle className="text-[15px] flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-slate-400" /> Quản
                        lý Hợp đồng lao động
                      </CardTitle>
                      {hasPermission("employeeContracts", "create") && (
                        <Button size="sm" className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700" data-testid="contract-add-btn" onClick={() => setContractForm({ open: true })}>
                          <Plus className="w-3.5 h-3.5 mr-1" /> Thêm hợp đồng
                        </Button>
                      )}
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="border border-slate-100 rounded-lg overflow-hidden">
                        <EmployeeContractList
                          userId={d.id || userId}
                          hideHeader={true}
                          onEdit={hasPermission("employeeContracts", "update") ? (c) => setContractForm({ open: true, data: c }) : undefined}
                        />
                        <EmployeeContractForm
                          open={contractForm.open}
                          onOpenChange={(open) => setContractForm((f) => ({ ...f, open }))}
                          userId={d.id || userId}
                          initialData={contractForm.data}
                          isSubmitting={savingContract}
                          onSubmit={async (values: any) => {
                            setSavingContract(true);
                            try {
                              if (contractForm.data?.id) {
                                await trpcClient.employeeContracts.update.mutate({ id: contractForm.data.id, data: values });
                              } else {
                                await trpcClient.employeeContracts.create.mutate({ ...values, userId: d.id || userId });
                              }
                              await queryClient.invalidateQueries({ queryKey: ["employee-contracts", d.id || userId] });
                              toast.success(contractForm.data?.id ? "Đã cập nhật hợp đồng" : "Đã thêm hợp đồng");
                              setContractForm({ open: false });
                            } catch (err: any) {
                              toast.error(err?.message || "Lưu hợp đồng thất bại");
                            } finally {
                              setSavingContract(false);
                            }
                          }}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              )}

              <TabsContent value="history" className="space-y-6">
                {/* Lịch sử cập nhật hồ sơ người dùng (vai trò, trạng thái, thông tin...) */}
                {(user as any)?.id && <ActivityLogSection entityType="user" entityId={(user as any).id} />}

                {/* Active Sessions Widget */}
                {activeSessions && activeSessions.length > 0 && (
                  <Card className="border-emerald-100 bg-emerald-50/30 shadow-sm">
                    <CardHeader className="pb-2 pt-4">
                      <CardTitle className="text-[15px] text-emerald-800 flex items-center gap-2">
                        <span className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse" />
                        Các phiên làm việc đang trực tuyến (
                        {activeSessions.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 pb-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {activeSessions.map((s: any) => (
                          <div
                            key={s.id}
                            className="flex items-center gap-3 p-3 bg-white rounded border border-emerald-100"
                          >
                            <Monitor className="w-5 h-5 text-emerald-600 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-slate-800 break-words whitespace-normal">
                                {parseUA(s.userAgent)}
                              </div>
                              <div className="text-xs text-slate-500">
                                {s.ipAddress || "—"}
                              </div>
                            </div>
                            <Badge
                              variant="secondary"
                              className="bg-emerald-100 text-emerald-700"
                            >
                              Online
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Login History */}
                <Card className="border-slate-200 shadow-sm overflow-hidden">
                  <CardHeader className="pb-3 border-b border-slate-100 bg-slate-50">
                    <CardTitle className="text-[15px] flex items-center gap-2">
                      <Globe className="w-4 h-4 text-slate-400" /> Lịch sử đăng
                      nhập / đăng xuất
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {activeTab === "history" && userActivity === undefined ? (
                      <div className="text-center text-slate-400 text-sm py-12 flex flex-col items-center gap-2">
                        <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />{" "}
                        Đang tải dữ liệu...
                      </div>
                    ) : !userActivity || userActivity.length === 0 ? (
                      <div className="text-center text-slate-400 text-sm py-12">
                        Chưa có dữ liệu lịch sử phiên làm việc
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {userActivity.slice(0, 10).map((a: any) => {
                          const isLogin = a.action === "login";
                          let meta: any = {};
                          try {
                            meta = a.newValue ? JSON.parse(a.newValue) : {};
                          } catch {
                            /* ignore */
                          }
                          return (
                            <div
                              key={a.id}
                              className="flex items-center gap-4 py-3 px-6 hover:bg-slate-50 transition-colors"
                            >
                              <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isLogin ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-500"}`}
                              >
                                {isLogin ? (
                                  <LogIn className="w-4 h-4" />
                                ) : (
                                  <LogOut className="w-4 h-4" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`text-sm font-semibold ${isLogin ? "text-emerald-700" : "text-slate-700"}`}
                                  >
                                    {isLogin
                                      ? "Đăng nhập thành công"
                                      : "Đăng xuất hệ thống"}
                                  </span>
                                  {meta.ip && (
                                    <Badge
                                      variant="outline"
                                      className="font-mono text-[10px] py-0 border-slate-200 text-slate-500 bg-white"
                                    >
                                      {meta.ip}
                                    </Badge>
                                  )}
                                </div>
                                {meta.userAgent && (
                                  <div className="text-xs text-slate-500 mt-1 font-medium">
                                    {parseUA(meta.userAgent)}
                                  </div>
                                )}
                              </div>
                              <div className="text-xs font-medium text-slate-400 shrink-0 tabular-nums">
                                {formatDate(a.createdAt)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Activity Log */}
                <div className="w-full pt-4"></div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </StandardDetailLayout>

      <ConfirmDialog
        open={showDelete}
        onOpenChange={setShowDelete}
        title="Xác nhận xoá người dùng?"
        description="Hành động này không thể hoàn tác. Dữ liệu liên quan có thể bị ảnh hưởng."
        onConfirm={handleDelete}
        isLoading={isDeleting}
        variant="destructive"
      />

      <ConfirmDialog
        open={showResetPassword}
        onOpenChange={setShowResetPassword}
        title="Xác nhận đặt lại mật khẩu?"
        description="Mật khẩu của người dùng sẽ được đặt lại về mặc định (123456)."
        onConfirm={() => resetPasswordMutation.mutate({ id: userId })}
        isLoading={resetPasswordMutation.isPending}
        variant="default"
      />
    </>
  );
}
